import { useEffect } from 'react';
import { Checkbox } from '@nextui-org/react';
import { Plus, Trash2 } from 'lucide-react';
import Button from '../../../ui/button';
import Input from '../../../ui/input';
import Switch from '../../../ui/switch';
import Text from '../../../ui/text';
import RadioGroup from '../../../ui/radio-group';
import { useFormContext, useFieldArray } from "react-hook-form";
import { healthConditionsCardio, healthConditionsDiabetes } from '../constants';

function HealthAssistance() {
  const { 
    watch,
    register,
    control,
    resetField,
    formState: { errors },
  } = useFormContext();

  const { fields: cardioFields, replace: cardioReplace,  } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormProvider)
    name: "healthConditionsCardio", // unique name for your Field Array
  });

  const { fields: diabetesFields, replace: diabetesReplace } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormProvider)
    name: "healthConditionsDiabetes", // unique name for your Field Array
  });

  const { fields: specialistsFields, append: appendSpecialist, remove: removeSpecialist } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormProvider)
    name: "specialistsInformation", // unique name for your Field Array
  });

  const watchHasMedicaid = watch('hasMedicaid');
  const watchHasChronicCondition = watch('hasChronicCondition');
  const watchSpecialistInformation = watch('specialistInformation');

  useEffect(() => {
    if (watchSpecialistInformation) {
      appendSpecialist();
    } else {
      removeSpecialist();
    }
  }, [watchSpecialistInformation]);

  useEffect(() => {
    if (!watchHasMedicaid) {
      resetField('medicaidNumber');
    }
  }, [watchHasMedicaid]);

  useEffect(() => {
    if (!watchHasChronicCondition) {
      cardioReplace(healthConditionsCardio);
    }
    if (!watchHasChronicCondition) {
      diabetesReplace(healthConditionsDiabetes);
    }
  }, [watchHasChronicCondition]);

  return (
    <section className="w-full">
      <h2 className="text-2xl font-semibold leading-none text-secondary">
        <Text>For Special Needs Plan (SNP) only</Text>
      </h2>
      <p className='mt-3'>
        <Text>If you are enrolling in the Solis Healthy Living Plan (HMO), you do not need to complete this section. D-SNPs are for dual eligible individuals and C-SNPs are for individuals with chronic conditions.</Text>
      </p>
      <div className='w-full flex flex-col justify-start items-start mt-5'>
        <Switch name='hasMedicaid' control={control}>
          <Text>Do you have Medicaid or receive assistance from a state plan under Medicaid?</Text>
        </Switch>
      </div>
      {watchHasMedicaid && (
        <>
          <div className='mt-3'>
            <p>
              <Text>D-SNPs are SNPs that restrict enrollment to individuals who are entitled to both Medicare and medical assistance from a state plan under Medicaid.</Text>
            </p>
          </div>
          <div className="grid grid-cols-12 gap-5 mt-5">
            <div className='col-span-12 sm:col-span-6 flex flex-col justify-start items-start'>
              <Text type='label' htmlFor='medicaidNumber'>Medicaid Number *</Text>
              <Input
                {...register('medicaidNumber')}
                size='sm'
                label={<Text>Medicaid Number</Text>}
                placeholder="-"
                errorMessage={errors?.medicaidNumber?.message || null}
                isInvalid={errors?.medicaidNumber ? true : false}
              />
            </div>
          </div>
        </>
      )}
      <div className='w-full flex flex-col justify-start items-start mt-10'>
        <Switch name='hasChronicCondition' control={control} >
          <div><Text>Do you have a chronic health condition such as a cardiovascular disease, chronic health failure (CHF), or diabetes?</Text></div>
          {(errors.healthConditionsCardio || errors.healthConditionsDiabetes) && (<div className='text-danger'><Text>Please, select at least one option.</Text></div>)}
        </Switch>
      </div>
      {watchHasChronicCondition && (
        <>
          <div className='mt-3'>
            <p>
              <Text>C-SNPs are SNPs that restrict enrollment to special needs individuals with specific severe or disabling chronic conditions.</Text>
            </p>
          </div>
          <div className='mt-5 pl-5 w-full flex flex-col gap-3'>
            <Switch name='hasCardioOrDiabetes' control={control} size='sm' >
              <div><Text>Have you ever been diagnosed with a cardiovascular disease (CVD), chronic heart failure (CHF), and/or diabetes?</Text></div>
              {(errors.hasCardioOrDiabetes || errors.hasCardioOrDiabetes) && (<div className='text-danger'><Text>Please, select at least one option.</Text></div>)}
            </Switch>
            <div className="text-base font-bold leading-loose text-zinc-600 max-md:mt-10 max-md:max-w-full">
              <Text>Cardiovascular Disease (CVD)/Chronic Heart Failure (CHF)</Text>
            </div>
            {healthConditionsCardio && cardioFields.map((field, index) => (
              <div key={index} className='w-full flex flex-row gap-2 justify-start'>
                <div>
                  <Checkbox
                    id={`healthConditionsCardio.${index}.checked`}
                    {...register(`healthConditionsCardio.${index}.checked`)}
                    aria-label={<Text>Option</Text>}
                    label=''
                    //value={field.value} 
                  >
                    <span>{field.label}</span>
                  </Checkbox>
                </div>
              </div>
            ))}
            <div className="mt-5 text-base font-bold leading-loose text-zinc-600 max-md:mt-10 max-md:max-w-full">
              <Text>Diabetes</Text>
            </div>
            {healthConditionsDiabetes && diabetesFields.map((field, index) => (
              <div key={index} className='w-full flex flex-row gap-2 justify-start'>
                <div>
                  <Checkbox
                    id={`healthConditionsDiabetes.${index}.checked`}
                    {...register(`healthConditionsDiabetes.${index}.checked`)}
                    aria-label={<Text>Option</Text>}
                    label=''   
                  >
                    <span>{field.label}</span>
                  </Checkbox>
                </div>
              </div>
            ))}
            <h2 className="text-2xl font-semibold leading-none text-gray-600 mt-5">
              <Text>Specialist Information</Text>
            </h2>
            <div className="grid grid-cols-12 gap-5">
              <div className='col-span-12 flex flex-row justify-between items-center gap-3'>
                <div className='flex flex-col justify-start items-start gap-3'>
                  <Text type='label' htmlFor='specialistInformation'>Do you have any specialist(s) that you see for the chronic conditions specified.</Text>
                  <RadioGroup
                    control={control}
                    name='specialistInformation'
                    size='sm'
                    defaultValue="no"
                    options={[
                      { label: <Text>Yes</Text>, value: true },
                      { label: <Text>No</Text>, value: false },
                    ]} 
                    isInvalid={!!errors?.PCPPatient}
                    errorMessage={errors?.PCPPatient?.message || null}
                  />
                </div>
                <div>
                  {watchSpecialistInformation && specialistsFields.length < 10 && (
                    <div className='w-full flex flex-row justify-end mt-5'>
                      <div className='rounded-full p-3 bg-primary cursor-pointer' onClick={() => appendSpecialist()}>
                        <Plus size={24} className='text-white' />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {watchSpecialistInformation && (
              <div className='w-full flex flex-col gap-3'>      
                {specialistsFields.map((field, index) => (
                  <div key={index} className='flex flex-row gap-5'>
                    <div className="flex-1 grid grid-cols-12 gap-5 mt-5 border-b border-gray-300 pb-5">
                      <div className='col-span-12 sm:col-span-4 flex flex-col justify-start items-start'>
                        <Text type='label' htmlFor={`specialistsInformation.${index}.name`}>Name *</Text>
                        <Input
                          {...register(`specialistsInformation.${index}.name`)}
                          size='sm'
                          label={<Text>Name</Text>}
                          placeholder="-"
                          errorMessage={errors?.specialistsInformation?.[index]?.name?.message || null}
                          isInvalid={errors?.specialistsInformation?.[index]?.name ? true : false}
                        />
                      </div>
                      <div className='col-span-12 sm:col-span-4 flex flex-col justify-start items-start'>
                        <Text type='label' htmlFor={`specialistsInformation.${index}.type`}>Type *</Text>
                        <Input
                          {...register(`specialistsInformation.${index}.type`)}
                          size='sm'
                          label={<Text>Type</Text>}
                          placeholder="-"
                          errorMessage={errors?.specialistsInformation?.[index]?.type?.message || null}
                          isInvalid={errors?.specialistsInformation?.[index]?.type ? true : false}
                        />
                      </div>
                      <div className='col-span-12 sm:col-span-4 flex flex-col justify-start items-start'>
                        <Text type='label' htmlFor={`specialistsInformation.${index}.phone`}>Telephone *</Text>
                        <Input
                          {...register(`specialistsInformation.${index}.phone`)}
                          size='sm'
                          label={<Text>Telephone</Text>}
                          placeholder="-"
                          errorMessage={errors?.specialistsInformation?.[index]?.phone?.message || null}
                          isInvalid={errors?.specialistsInformation?.[index]?.phone ? true : false}
                        />
                      </div>
                      <div className='col-span-12 flex flex-col justify-start items-start'>
                        <Text type='label' htmlFor={`specialistsInformation.${index}.address`}>Address *</Text>
                        <Input
                          {...register(`specialistsInformation.${index}.address`)}
                          size='sm'
                          label={<Text>Address</Text>}
                          placeholder="-"
                          errorMessage={errors?.specialistsInformation?.[index]?.address?.message || null}
                          isInvalid={errors?.specialistsInformation?.[index]?.address ? true : false}
                        />
                      </div>
                    </div>
                    <div className=''>
                      {specialistsFields.length > 1 && (
                        <div>
                          <div onClick={() => removeSpecialist(index)} className='cursor-pointer hidden md:block'>
                            <Trash2 size={24} className='text-gray-400' />
                          </div>  
                          <div className='cursor-pointer flex flex-row md:hidden'>
                            <Button onClick={() => removeSpecialist(index)}>
                              <Text>Delete</Text> 
                              <Trash2 size={24} className='text-white' />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

export default HealthAssistance;